import { DisconnectOutlined } from '@ant-design/icons';
import {
  ProductDoc,
  ProductInventoryDoc,
  ProductInventoryUnit,
  ProductStateCode,
  ProductSupplierDoc,
} from '@gooduncles/gu-app-schema';
import { Button, Descriptions, Empty, Modal, Space, Tag, message } from 'antd';
import { FC, useState } from 'react';

import { formatNumber } from 'src/utils/common';
import { updateProduct } from 'src/utils/firebase/firebase-short-cut';

import { ProductInventoryWithDetail } from 'src/hooks/subscription/useProductInventoriesWithDetail';

import AlgoliaSearch from 'src/atomic-components/molecules/AlgoriaSearch/AlgoriaSearch';

import DisconnectProductModal from './DisconnectProductModal';

interface ProductInfoProps {
  inventory: ProductInventoryDoc;
  unit: ProductInventoryUnit;
  product?: ProductDoc;
  inventories?: ProductInventoryWithDetail[];
  productSuppliers: ProductSupplierDoc[];
}

const ProductInfo: FC<ProductInfoProps> = ({ inventory, unit, product, inventories = [], productSuppliers }) => {
  const ratio = inventory.uom[unit]?.ratio ?? 1;
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDisconnectModalVisible, setIsDisconnectModalVisible] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [selectedProductName, setSelectedProductName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onAddProduct = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setSelectedProductId(null);
    setSelectedProductName(null);
  };

  const handleProductSearch = (productId: string, productFullName: string) => {
    setSelectedProductId(productId);
    setSelectedProductName(productFullName);
  };

  const handleConfirm = async () => {
    if (!selectedProductId) {
      message.error('연결할 상품을 선택해주세요.');
      return;
    }

    setLoading(true);
    try {
      // 상품 연결 로직 - 인벤토리에 상품 ID 추가
      await updateProduct(selectedProductId, {
        inventoryId: inventory._id,
        inventoryUnit: unit,
      });

      message.success('상품이 성공적으로 연결되었습니다.');
      setIsModalVisible(false);
    } catch (error) {
      console.error('상품 연결 실패:', error);
      message.error('상품 연결에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 상품 연결 해지 모달 열기
  const openDisconnectModal = () => {
    setIsDisconnectModalVisible(true);
  };

  // 상품 연결 해지 모달 닫기
  const closeDisconnectModal = () => {
    setIsDisconnectModalVisible(false);
  };

  if (!product) {
    return (
      <Space direction='vertical' align='center' style={{ width: '100%' }}>
        <Empty description='연결된 상품 정보가 없습니다' />
        <Button type='primary' onClick={onAddProduct}>
          상품 연결하기
        </Button>

        <Modal
          title='상품 연결하기'
          open={isModalVisible}
          onCancel={handleCancel}
          footer={[
            <Button key='cancel' onClick={handleCancel}>
              취소
            </Button>,
            <Button key='submit' type='primary' loading={loading} disabled={!selectedProductId} onClick={handleConfirm}>
              연결하기
            </Button>,
          ]}>
          <Space direction='vertical' style={{ width: '100%' }}>
            <p>연결할 상품을 검색해주세요:</p>
            <AlgoliaSearch placeholder='상품코드 또는 상품명으로 검색' onSearch={handleProductSearch} />
            {selectedProductName && (
              <div style={{ marginTop: '10px', padding: '10px', border: '1px solid #f0f0f0', borderRadius: '4px' }}>
                <p>
                  <strong>선택된 상품:</strong> {selectedProductName}
                </p>
                <p>
                  <strong>상품 ID:</strong> {selectedProductId}
                </p>
              </div>
            )}
          </Space>
        </Modal>
      </Space>
    );
  }

  return (
    <>
      <Descriptions title={`${inventory.inventoryName} ${product.inventoryUnit}`} column={1}>
        <Descriptions.Item
          label={
            <Space>
              상품명
              <Button
                type='text'
                icon={<DisconnectOutlined />}
                size='small'
                onClick={openDisconnectModal}
                title='상품 연결 해지'
              />
            </Space>
          }>
          {product.fullName}
        </Descriptions.Item>
        <Descriptions.Item label={`가용 재고(${inventory.quantity} / ${ratio})`}>
          {formatNumber(inventory.quantity / ratio, 3)} {product.inventoryUnit}
        </Descriptions.Item>
        <Descriptions.Item label='판매 상태'>
          <Tag color={product.state === ProductStateCode.DISCONTINUED ? 'red' : 'green'}>
            {product.state === ProductStateCode.DISCONTINUED ? '판매중지' : '판매중'}
          </Tag>
        </Descriptions.Item>
      </Descriptions>
      {/* 상품 연결 해지 모달 */}
      {product && (
        <DisconnectProductModal
          isVisible={isDisconnectModalVisible}
          onCancel={closeDisconnectModal}
          product={product}
          inventory={inventory}
          unit={unit}
          inventories={inventories}
          productSuppliers={productSuppliers}
        />
      )}
    </>
  );
};

export default ProductInfo;
